import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquare, Send, Undo2, Loader2, Check, XCircle, PanelRightClose, PanelRightOpen, Eye } from "lucide-react";
import {
  chatInterpret, chatExecute, chatUndo, chatGetLatestSession, chatCreateSession,
  createScenarioFromChat, fetchScenarios, deleteScenario as apiDeleteScenario,
  type ChatInterpretResponse, type ChatChange, type ScenarioResponse,
} from "../api/client";
import ChangeConfirmationModal from "./ChangeConfirmationModal";
import { useAuth } from "../contexts/AuthContext";
import { prettyFieldLabel } from "../utils/labels";
import { fmtFieldValue } from "../utils/fieldValueFormat";
import { fieldToTab, flashChanges } from "../utils/changeLocation";

interface ChatMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  changes?: ChatChange[];
  pendingConfirm?: boolean;
  impactSummary?: string;
  showSourceLink?: boolean;
  ephemeral?: boolean;
}

interface UndoEntry {
  scenarioAssumptions: Record<string, unknown>;
  dealFields: Record<string, unknown>;
  changes: ChatChange[];
  description: string;
}

interface Props {
  dealId: string;
  scenarioId: string;
  scenarioName?: string;
  scenarioAssumptions: Record<string, unknown> | null;
  dealPropertyData: Record<string, unknown> | null;
  activeTab?: string;
  collapsed: boolean;
  onToggle: () => void;
  onProjectionUpdated: () => Promise<void> | void;
  onScenarioCreated?: (s: ScenarioResponse) => void;
}

let _msgId = 0;
function nextId() { return `msg-${++_msgId}`; }

function findLastIdx<T>(arr: T[], pred: (v: T) => boolean): number {
  for (let i = arr.length - 1; i >= 0; i--) { if (pred(arr[i])) return i; }
  return -1;
}

// Strip the "[Changes: field=value; ...]" suffix the backend appends to
// assistant history for multi-turn Claude context. Brokers should never see
// it in the chat bubble. Matches both a trailing paragraph starting with
// "[Changes:" and the inline form right after the message text.
function cleanMessage(text: string | undefined | null): string {
  if (!text) return "";
  return text.replace(/\s*\[Changes:[\s\S]*?\]\s*$/, "").trim();
}

/** Simple markdown renderer for chat messages: bold, bullet lists, horizontal rules. */
function renderMarkdown(text: string): React.ReactNode {
  const lines = text.split("\n");
  const elements: React.ReactNode[] = [];
  let listItems: string[] = [];

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <ul key={`ul-${elements.length}`} className="list-disc list-inside space-y-0.5 my-1">
          {listItems.map((item, i) => <li key={i}>{inlineMd(item)}</li>)}
        </ul>
      );
      listItems = [];
    }
  };

  const inlineMd = (s: string): React.ReactNode => {
    // Bold: **text** or __text__
    const parts = s.split(/(\*\*[^*]+\*\*|__[^_]+__)/g);
    if (parts.length === 1) return s;
    return parts.map((part, i) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith("__") && part.endsWith("__")) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    // Horizontal rule
    if (/^---+$/.test(line.trim())) {
      flushList();
      elements.push(<hr key={`hr-${i}`} className="border-grove-200 my-2" />);
      continue;
    }
    // Bullet list item
    if (/^[-•]\s/.test(line.trim())) {
      listItems.push(line.trim().replace(/^[-•]\s/, ""));
      continue;
    }
    // Regular line
    flushList();
    if (line.trim() === "") {
      elements.push(<br key={`br-${i}`} />);
    } else {
      elements.push(<span key={`p-${i}`}>{inlineMd(line)}{i < lines.length - 1 ? "\n" : ""}</span>);
    }
  }
  flushList();
  return <>{elements}</>;
}

/** Format a field name for display, stripping prefixes and adding context. */
function displayFieldName(fieldName: string): string {
  let prefix = "";
  let name = fieldName
    .replace(/^_/, "") // meta-actions: "_add_tenant" → "Add Tenant"
    .replace(/^rollover_assumptions\./, "")
    .replace(/^rollover_override\./, "")
    .replace(/^debt\./, "")
    .replace(/^override\./, "")
    .replace(/^opex\./, "");
  if (name.startsWith("cam_structure.")) {
    prefix = "CAM ";
    name = name.replace(/^cam_structure\./, "");
  }
  const ym = name.match(/^(.+)\.(\d+)$/);
  let suffix = "";
  if (ym) {
    name = ym[1];
    suffix = ` (Year ${ym[2]})`;
  }
  return prefix + prettyFieldLabel(name) + suffix;
}

function formatChange(c: ChatChange): string {
  const fieldName = c.field_name || "";
  const name = displayFieldName(fieldName);
  const prefix = c.tenant_name ? `${c.tenant_name} → ` : "";
  if (c.old_value !== undefined && c.old_value !== null) {
    return `${prefix}${name}: ${fmtFieldValue(c.old_value, fieldName)} → ${fmtFieldValue(c.new_value, fieldName)}`;
  }
  return `${prefix}${name}: ${fmtFieldValue(c.new_value, fieldName)}`;
}

export default function ChatPanel({
  dealId, scenarioId, scenarioName, scenarioAssumptions, dealPropertyData,
  activeTab, collapsed, onToggle, onProjectionUpdated, onScenarioCreated,
}: Props) {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const isAdmin = authUser?.is_admin ?? false;
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [undoStack, setUndoStack] = useState<UndoEntry[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [pendingResponse, setPendingResponse] = useState<ChatInterpretResponse | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Synchronous mirror of `loading`. The gate in `send()` must read a ref,
  // not state: when a streaming response resolves, `setLoading(false)` is
  // scheduled but not flushed until the next render, and a rapid second
  // submit can fire in that window. The state-closure `loading` would still
  // be true and the submit handler would silently drop the request.
  const loadingRef = useRef(false);
  const setLoadingSync = (v: boolean) => { loadingRef.current = v; setLoading(v); };

  /** Show technical error details only to admins; regular users get friendly messages. */
  const friendlyError = useCallback((raw: string, fallback?: string) => {
    if (isAdmin) return raw;
    return fallback || "Something went wrong. Please try again or rephrase your request.";
  }, [isAdmin]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // Load existing session on mount
  useEffect(() => {
    let cancelled = false;
    chatGetLatestSession(dealId).then(s => {
      if (cancelled) return;
      setSessionId(s.session_id);
      if (s.messages?.length) {
        setMessages(s.messages.map(m => ({
          id: nextId(),
          role: m.role as "user" | "assistant",
          content: m.role === "assistant" ? cleanMessage(m.content) : m.content,
        })));
      } else {
        setMessages([{ id: nextId(), role: "system", content: "Ask me to modify the model — e.g. \"set vacancy to 7%\" or \"change Walgreens rent to $30/SF\"" }]);
      }
    }).catch(() => {
      if (cancelled) return;
      setMessages([{ id: nextId(), role: "system", content: "Ask me to modify the model — e.g. \"set vacancy to 7%\" or \"stress test exit cap rate\"" }]);
    });
    return () => { cancelled = true; };
  }, [dealId]);

  // Collapsed rail
  if (collapsed) {
    return (
      <button onClick={onToggle} className="w-10 h-full bg-grove-50 border-l border-grove-300 flex flex-col items-center justify-center hover:bg-grove-100 transition-colors" title="Open AutoVal Chat">
        <PanelRightOpen className="w-4 h-4 text-forest-600 mb-2" />
        <span className="text-[10px] font-semibold text-bark-500 writing-mode-vertical" style={{ writingMode: "vertical-lr" }}>AutoVal Chat</span>
      </button>
    );
  }

  const pushUndo = (changes?: ChatChange[]) => {
    setUndoStack(prev => [...prev, {
      scenarioAssumptions: { ...(scenarioAssumptions ?? {}) },
      // Only true deal-level fields are snapshotted. exit_cap_rate,
      // discount_rate, and selling_costs_pct now write to scenario
      // assumptions (chat.py SCENARIO_FIELDS), so they're covered by the
      // scenarioAssumptions snapshot above — reading them off
      // dealPropertyData here would restore stale values on undo.
      dealFields: dealPropertyData ? { purchase_price: dealPropertyData.purchase_price, going_in_cap_rate: dealPropertyData.going_in_cap_rate, hold_period_years: dealPropertyData.hold_period_years, general_vacancy_pct: dealPropertyData.general_vacancy_pct } : {},
      changes: changes || [],
      description: input || "change",
    }]);
  };

  const handleBuiltIn = async (text: string): Promise<boolean> => {
    const l = text.toLowerCase().trim();
    // Clear chat history and start a new session
    const clearPhrases = ["clear", "clear history", "new conversation", "start over"];
    if (clearPhrases.includes(l)) {
      setLoadingSync(true);
      try {
        const s = await chatCreateSession(dealId);
        setSessionId(s.session_id);
        setUndoStack([]);
        setPendingResponse(null);
        setMessages([
          { id: nextId(), role: "system", content: "Ask me to modify the model — e.g. \"set vacancy to 7%\" or \"change Walgreens rent to $30/SF\"" },
          { id: nextId(), role: "assistant", content: "Chat history cleared. Starting a new conversation." },
        ]);
      } catch (e: any) {
        setMessages(p => [...p, { id: nextId(), role: "assistant", content: friendlyError(`Failed to start new session: ${e.message}`, "I wasn't able to start a new conversation. Please try again.") }]);
      } finally { setLoadingSync(false); }
      return true;
    }
    if (l === "undo" || l === "undo last change") {
      if (!undoStack.length) { setMessages(p => [...p, { id: nextId(), role: "assistant", content: "Nothing to undo." }]); return true; }
      const entry = undoStack[undoStack.length - 1];
      setLoadingSync(true);
      try {
        // Build reverse changes. Meta-actions (_add_tenant, _remove_tenant,
        // _create_profile, ...) carry a server-computed `inverse` operation
        // that restores the prior state via the normal /execute flow; scalar
        // changes are reversed by swapping old_value ↔ new_value.
        const reverseChanges: ChatChange[] = [];
        for (const c of entry.changes) {
          const inverse = (c as ChatChange & { inverse?: ChatChange }).inverse;
          if (inverse && inverse.field_name) {
            reverseChanges.push(inverse);
          } else if (!(c.field_name || "").startsWith("_") && c.old_value !== undefined && c.old_value !== null) {
            reverseChanges.push({ ...c, new_value: c.old_value, old_value: c.new_value });
          }
        }

        if (reverseChanges.length > 0) {
          // Apply reverse changes via execute endpoint
          await chatExecute(dealId, scenarioId, reverseChanges);
        } else {
          // Fallback: restore full snapshot
          await chatUndo(dealId, scenarioId, entry.scenarioAssumptions, entry.dealFields);
        }
        setUndoStack(p => p.slice(0, -1));
        await onProjectionUpdated();
        // Build descriptive revert message
        const revertDesc = entry.changes.length > 0
          ? entry.changes.map(c => {
              const name = displayFieldName(c.field_name || "");
              return `${name}: ${fmtFieldValue(c.new_value, c.field_name || "")} → ${fmtFieldValue(c.old_value, c.field_name || "")}`;
            }).join(", ")
          : entry.description;
        setMessages(p => [...p, { id: nextId(), role: "assistant", content: `Reverted: ${revertDesc}` }]);
      } catch (e: any) { setMessages(p => [...p, { id: nextId(), role: "assistant", content: friendlyError(`Undo failed: ${e.message}`, "I wasn't able to undo that change. Please try again.") }]); }
      finally { setLoadingSync(false); }
      return true;
    }
    const saveMatch = l.match(/^save as scenario\s+(.+)$/);
    if (saveMatch) {
      setLoadingSync(true);
      try {
        const s = await createScenarioFromChat(dealId, scenarioId, saveMatch[1]);
        onScenarioCreated?.(s);
        setMessages(p => [...p, { id: nextId(), role: "assistant", content: `Saved scenario "${saveMatch[1]}". Taking you to the Scenarios view.` }]);
        // Navigate to scenarios page after creation
        setTimeout(() => navigate(`/deals/${dealId}/scenarios`), 600);
      } catch (e: any) { setMessages(p => [...p, { id: nextId(), role: "assistant", content: friendlyError(`Failed to save scenario: ${e.message}`, "I wasn't able to save that scenario. Please try again.") }]); }
      finally { setLoadingSync(false); }
      return true;
    }
    // Delete scenario by name
    const deleteMatch = l.match(/^delete scenario\s+(.+)$/);
    if (deleteMatch) {
      const name = deleteMatch[1].trim();
      setLoadingSync(true);
      try {
        const allScenarios = await fetchScenarios(dealId);
        const target = allScenarios.find(s => s.name.toLowerCase() === name.toLowerCase());
        if (!target) {
          setMessages(p => [...p, { id: nextId(), role: "assistant", content: `No scenario found named "${name}". Available: ${allScenarios.map(s => s.name).join(", ")}` }]);
        } else {
          await apiDeleteScenario(dealId, target.id);
          onScenarioCreated?.(target); // triggers refresh
          setMessages(p => [...p, { id: nextId(), role: "assistant", content: `Deleted scenario "${target.name}".` }]);
        }
      } catch (e: any) { setMessages(p => [...p, { id: nextId(), role: "assistant", content: friendlyError(`Failed to delete: ${e.message}`, "I wasn't able to delete that scenario. Please try again.") }]); }
      finally { setLoadingSync(false); }
      return true;
    }
    return false;
  };

  // If a confirmation is pending, textual confirm/cancel should behave like the
  // buttons; anything else must clear the pending state and proceed as a fresh
  // instruction. This prevents the chat from silently blocking input when a
  // stale pending state is around — session restore doesn't resurface the
  // Confirm/Cancel buttons, so users must be able to break out by typing.
  const CONFIRM_WORDS = ["confirm", "yes", "ok", "okay", "go ahead", "do it", "apply", "accept"];
  const CANCEL_WORDS = ["cancel", "no", "nope", "never mind", "nevermind", "skip", "abort", "discard"];

  const clearPendingState = () => {
    setPendingResponse(null);
    setMessages(p => {
      const u = [...p];
      const idx = findLastIdx(u, (m: ChatMessage) => !!m.pendingConfirm);
      if (idx >= 0) u[idx] = { ...u[idx], pendingConfirm: false };
      return u;
    });
  };

  const send = async () => {
    const text = input.trim();
    if (!text || loadingRef.current) return;
    setInput("");
    setMessages(p => [...p, { id: nextId(), role: "user", content: text }]);

    // Intercept textual confirm/cancel while a response is pending.
    if (pendingResponse) {
      const l = text.toLowerCase();
      if (CONFIRM_WORDS.includes(l)) {
        await confirmPending();
        return;
      }
      if (CANCEL_WORDS.includes(l)) {
        cancelPending();
        return;
      }
      clearPendingState();
    }

    if (await handleBuiltIn(text)) return;

    // Compound requests (multi-clause instructions) can take up to ~60s on the
    // Claude round-trip. Show an ephemeral acknowledgment so the user sees
    // something happened; the response replaces it in finally.
    const isCompound = (text.match(/,/g) || []).length >= 2 || (/ and /i.test(text) && text.length > 40);
    const ackId = isCompound ? nextId() : null;
    if (ackId) {
      setMessages(p => [...p, {
        id: ackId,
        role: "assistant",
        content: "Working on it — that's a few changes, so it may take up to a minute...",
        ephemeral: true,
      }]);
    }

    setLoadingSync(true);
    try {
      const res = await chatInterpret(dealId, text, scenarioId, sessionId ?? undefined, activeTab);
      if (res.session_id) setSessionId(res.session_id);

      if (res.action === "clarify") {
        setMessages(p => [...p, { id: nextId(), role: "assistant", content: cleanMessage(res.question) || "Could you clarify?" }]);
      } else if (res.action === "confirm") {
        setPendingResponse(res);
        setMessages(p => [...p, { id: nextId(), role: "assistant", content: cleanMessage(res.summary) || "Confirm these changes:", changes: res.changes, pendingConfirm: true }]);
      } else if (res.action === "execute") {
        // Only push undo & refresh projection when actual changes were made.
        // (With preview mode on, change-bearing responses come back as
        // "confirm" — this path now only carries Q&A and no-op responses.)
        if (res.changes && res.changes.length > 0) {
          pushUndo(res.changes);
          await onProjectionUpdated();
          flashChanges(res.changes);
        }
        setMessages(p => [...p, { id: nextId(), role: "assistant", content: cleanMessage(res.message) || "Done.", changes: res.changes, impactSummary: res.impact_summary ?? undefined, showSourceLink: (res.changes && res.changes.length > 0) || !!res.source_reference }]);
      } else {
        setMessages(p => [...p, { id: nextId(), role: "assistant", content: cleanMessage(res.message) || "I didn't quite catch that. Could you try rephrasing?" }]);
      }
    } catch (e: any) {
      setMessages(p => [...p, { id: nextId(), role: "assistant", content: friendlyError(
        `Error: ${e.message}`,
        "I'm having trouble processing your request. Please try rephrasing — for example, \"set vacancy to 7%\" or \"what's the current NOI?\"",
      ) }]);
    }
    finally {
      setLoadingSync(false);
      if (ackId) setMessages(p => p.filter(m => m.id !== ackId));
    }
  };

  const confirmPending = async () => {
    if (!pendingResponse) return;
    setLoadingSync(true);
    try {
      const res = await chatExecute(dealId, scenarioId, pendingResponse.changes, sessionId ?? undefined);
      // Push the undo entry AFTER execute so it carries the server-enriched
      // changes (old_value + inverse for meta-actions). The props read by
      // pushUndo still hold pre-change state until onProjectionUpdated runs.
      const applied = res.changes && res.changes.length > 0 ? res.changes : pendingResponse.changes;
      pushUndo(applied);
      await onProjectionUpdated();
      // Show where the changes landed: jump to the impacted tab and flash
      // the changed fields green.
      if (applied.length > 0) {
        const loc = fieldToTab(applied[0]);
        navigate(`/deals/${dealId}/${loc.route}`);
        flashChanges(applied);
      }
      setPendingResponse(null);
      setMessages(p => {
        const u = [...p];
        const idx = findLastIdx(u, (m: ChatMessage) => !!m.pendingConfirm);
        if (idx >= 0) u[idx] = { ...u[idx], pendingConfirm: false };
        return [...u, { id: nextId(), role: "assistant", content: cleanMessage(res.message) || "Applied.", changes: res.changes, impactSummary: res.impact_summary ?? undefined }];
      });
    } catch (e: any) {
      // Clear pendingResponse and remove confirm buttons so the input
      // re-enables — otherwise the chat locks until page refresh.
      setPendingResponse(null);
      setMessages(p => {
        const u = [...p];
        const idx = findLastIdx(u, (m: ChatMessage) => !!m.pendingConfirm);
        if (idx >= 0) u[idx] = { ...u[idx], pendingConfirm: false };
        return [...u, { id: nextId(), role: "assistant", content: friendlyError(`Failed to apply changes: ${e.message}`, "I wasn't able to apply those changes. Try rephrasing, or make one change at a time.") }];
      });
    }
    finally { setLoadingSync(false); }
  };

  const cancelPending = () => {
    setPendingResponse(null);
    setMessages(p => {
      const u = [...p];
      const idx = findLastIdx(u, (m: ChatMessage) => !!m.pendingConfirm);
      if (idx >= 0) u[idx] = { ...u[idx], pendingConfirm: false };
      return [...u, { id: nextId(), role: "assistant", content: "Cancelled." }];
    });
  };

  return (
    <div className="flex flex-col h-full max-h-screen bg-white border-l border-grove-300">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-grove-200 bg-grove-50">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-forest-600" />
          <span className="font-display text-sm font-semibold text-forest-800">AutoVal Chat</span>
        </div>
        <button onClick={onToggle} className="p-1 rounded-md text-bark-400 hover:text-bark-600 hover:bg-grove-200 transition-colors" title="Collapse">
          <PanelRightClose className="w-4 h-4" />
        </button>
      </div>
      {scenarioName && (
        <div className="px-4 py-1.5 border-b border-grove-200 bg-grove-50/50">
          <span className="text-[10px] font-medium text-bark-400 uppercase tracking-wider">Scenario:</span>
          <span className="ml-1.5 text-xs text-bark-600">{scenarioName}</span>
        </div>
      )}
      {undoStack.length > 0 && (
        <button onClick={() => handleBuiltIn("undo")} className="flex items-center gap-1 px-4 py-1.5 text-xs text-bark-500 hover:text-forest-600 border-b border-grove-200">
          <Undo2 className="w-3 h-3" /> Undo last change
        </button>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages.map(msg => (
          <div key={msg.id} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[90%] rounded-xl px-3 py-2 text-sm ${
              msg.role === "user" ? "bg-forest-600 text-white"
                : msg.role === "system" ? "bg-grove-100 text-bark-500 italic"
                : "bg-grove-50 text-bark-700 border border-grove-200"
            }`}>
              <div className="whitespace-pre-wrap">{msg.role === "assistant" ? renderMarkdown(msg.content) : msg.content}</div>
              {msg.changes && msg.changes.length > 0 && (
                <div className="mt-2 space-y-1">
                  {msg.changes.map((c, i) => (
                    <div key={i} className={`flex items-start gap-1.5 text-xs px-2 py-1 rounded-md ${msg.pendingConfirm ? "bg-honey-50 border border-honey-200" : "bg-green-50 border border-green-200"}`}>
                      <Check className={`w-3 h-3 mt-0.5 shrink-0 ${msg.pendingConfirm ? "text-honey-500" : "text-green-600"}`} />
                      <span className="font-mono text-bark-600">{formatChange(c)}</span>
                    </div>
                  ))}
                </div>
              )}
              {msg.impactSummary && (
                <p className="mt-2 text-xs opacity-80 border-t border-grove-200 pt-1.5">{msg.impactSummary}</p>
              )}
              {msg.showSourceLink && (
                <button
                  onClick={() => navigate(`/deals/${dealId}/audit`)}
                  className="mt-2 flex items-center gap-1 text-xs text-forest-700 hover:text-forest-900 border-t border-grove-200 pt-1.5"
                >
                  <Eye className="w-3 h-3" /> View in Audit
                </button>
              )}
              {msg.pendingConfirm && (
                <div className="mt-2 flex gap-2">
                  <button onClick={confirmPending} disabled={loading} className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-forest-600 text-white rounded-lg hover:bg-forest-700 disabled:opacity-50">
                    <Check className="w-3 h-3" /> Confirm
                  </button>
                  <button onClick={cancelPending} disabled={loading} className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-white text-bark-600 border border-grove-300 rounded-lg hover:bg-grove-50 disabled:opacity-50">
                    <XCircle className="w-3 h-3" /> Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="flex items-center gap-2 rounded-xl px-3 py-2 bg-grove-50 border border-grove-200">
              <Loader2 className="w-4 h-4 text-forest-600 animate-spin" />
              <span className="text-sm text-bark-500">Thinking...</span>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="border-t border-grove-200 px-3 py-2.5">
        <div className="flex items-end gap-2">
          <textarea ref={inputRef} value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={pendingResponse ? 'Type "confirm" to apply, "cancel" to discard, or a new instruction' : 'e.g. "set vacancy to 7%"'}
            disabled={loading} rows={1}
            className="flex-1 px-3 py-2 text-sm border border-grove-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-forest-300 disabled:bg-grove-50" />
          <button onClick={send} disabled={loading || !input.trim()}
            className="p-2 rounded-lg bg-forest-600 text-white hover:bg-forest-700 disabled:opacity-40">
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Change wizard: visual confirmation before any chat change is applied */}
      {pendingResponse && pendingResponse.changes.length > 0 && (
        <ChangeConfirmationModal
          changes={pendingResponse.changes}
          summary={cleanMessage(pendingResponse.summary || pendingResponse.message)}
          scenarioName={scenarioName}
          applying={loading}
          onApply={confirmPending}
          onCancel={cancelPending}
        />
      )}
    </div>
  );
}
